/**
 * Centralized hotkey configuration
 * Maps action types to their keyboard shortcuts and display labels
 */

export const HOTKEYS = {
  // Search and Navigation
  FOCUS_SEARCH: {
    keys: ['Ctrl / Cmd', 'F'],
    label: 'Ctrl  + F',
    description: 'Focus search input',
  },

  GLOBAL_SEARCH: {
    keys: ['Ctrl / Cmd', 'K'],
    label: 'Ctrl  + K',
    description: 'Global search',
    shortLabel: 'Ctrl/Cmd+K',
  },

  NEXT_PAGE: {
    keys: ['Ctrl / Cmd', 'Shift', '→'],
    label: 'Ctrl  + Shift + →',
    description: 'Next page/step',
  },

  PREV_PAGE: {
    keys: ['Ctrl / Cmd', 'Shift', '←'],
    label: 'Ctrl  + Shift + ←',
    description: 'Previous page/step',
  },

  // Template Row Actions (User & Admin)
  GENERATE_DOCUMENT: {
    keys: ['Ctrl / Cmd', 'Enter'],
    label: 'Ctrl  + Enter',
    description: 'Generate documents',
    shortLabel: 'Ctrl/Cmd+Enter',
  },

  PIN_TEMPLATE: {
    keys: ['[index]', 'p'],
    label: 'index + P',
    description: 'Pin / unpin template',
    shortLabel: '[idx]+p',
  },

  ROW_GENERATE: {
    keys: ['[index]', 'g'],
    label: 'index + G',
    description: 'Generate for row',
    shortLabel: '[idx]+g',
  },

  // Document Actions (Generate Drawer)
  OPEN_EDITOR: {
    keys: ['Ctrl / Cmd', 'O'],
    label: 'Ctrl  + O',
    description: 'Open in editor',
    shortLabel: 'Ctrl/Cmd+O',
  },

  DOWNLOAD_DOCX: {
    keys: ['Ctrl / Cmd', 'D'],
    label: 'Ctrl  + D',
    description: 'Download as DOCX',
    shortLabel: 'Ctrl/Cmd+D',
  },

  DOWNLOAD_PDF: {
    keys: ['Ctrl / Cmd', 'E'],
    label: 'Ctrl  + E',
    description: 'Download as PDF',
    shortLabel: 'Ctrl/Cmd+E',
  },

  PRINT_DOCUMENT: {
    keys: ['Ctrl / Cmd', 'P'],
    label: 'Ctrl  + P',
    description: 'Print document',
    shortLabel: 'Ctrl/Cmd+P',
  },

  // Form Actions (Template Set Creation)
  SUBMIT_FORM: {
    keys: ['Ctrl / Cmd', 'Enter'],
    label: 'Ctrl  + Enter',
    description: 'Next / Submit / Finish',
    shortLabel: 'Ctrl/Cmd+Enter',
  },

  ADD_ITEM: {
    keys: ['Ctrl / Cmd', 'Shift', 'N'],
    label: 'Ctrl  + Shift + N',
    description: 'Add placeholder / document',
    shortLabel: 'Ctrl/Cmd+Shift+N',
  },

  NEW_TEMPLATE_SET: {
    keys: ['Ctrl / Cmd', 'Shift', 'K'],
    label: 'Ctrl  + Shift + K',
    description: 'Open New Template Set',
    shortLabel: 'Ctrl/Cmd+Shift+K',
  },
};

/**
 * Get hotkey configuration by action type
 * @param {string} actionType - The action type key from HOTKEYS
 * @param {boolean} useShortLabel - Whether to return short label (default: false)
 * @returns {string|null} The hotkey label or null if not found
 */
export const getHotkeyLabel = (actionType, useShortLabel = false) => {
  const hotkey = HOTKEYS[actionType];
  if (!hotkey) return null;
  return useShortLabel ? hotkey.shortLabel : hotkey.label;
};

/**
 * Get full hotkey configuration object
 * @param {string} actionType - The action type key from HOTKEYS
 * @returns {object|null} The full hotkey config or null if not found
 */
export const getHotkeyConfig = (actionType) => {
  return HOTKEYS[actionType] || null;
};
